/**
 * Raw feature module (W2-05 easing-templates-raw; DESIGN.md §4.18 "Raw (Full)"; WORKPACKAGES Appendix B: `raw.*`).
 *
 * Provides the Raw dock tab of the Chart workspace (Full level) and the commands:
 *  - `raw.show`: show the Raw tab;
 *  - `raw.apply` (Ctrl+Enter while the Raw tab has focus, both presets) and `raw.revert`;
 *  - `raw.copy`: copy the selection's exported RPE JSON (works with the tab closed);
 *  - `raw.format`: compact JSON on / off (`ext.raw.compact`);
 * settings rows for the JSON layout and number style, and a help topic.
 */

import type {
  CommandContext,
  CommandDef,
  FeatureModule,
  HelpTopicDef,
  KeyBinding,
  KeymapPresetId,
  SettingDef,
} from '../../app/registry/types';
import type { MessageRef } from '../../core/util/types';
import { m } from './raw.messages';
import { RawPanel, targetLabel } from './RawPanel';
import { RAW_COMPACT_KEY, rawController, selectionJson } from './rawSession';

function projectOpen(ctx: CommandContext): true | MessageRef {
  return ctx.store !== null ? true : { key: m.noProject };
}

export const RAW_COMMANDS: readonly CommandDef[] = [
  {
    id: 'raw.show',
    title: m.cmdShow,
    category: 'View',
    icon: 'Braces',
    level: 'full',
    aliases: ['raw', 'json', 'raw json', 'rpe json', 'source', '原始', 'JSON'],
    menus: [{ menu: 'app.view', group: 'panels', order: 60 }],
    enabled: projectOpen,
    run(ctx) {
      ctx.ui.showPanel('raw');
    },
  },
  {
    id: 'raw.apply',
    title: m.cmdApply,
    category: 'Edit',
    icon: 'Check',
    level: 'full',
    aliases: ['apply json', 'apply raw'],
    when: 'focus.raw',
    hint: 5,
    enabled(ctx) {
      const ok = projectOpen(ctx);
      if (ok !== true) return ok;
      const c = rawController();
      if (c === null) return { key: m.panelClosed };
      return c.dirty ? true : { key: m.appliedNothing };
    },
    run() {
      rawController()?.apply();
    },
  },
  {
    id: 'raw.revert',
    title: m.cmdRevert,
    category: 'Edit',
    icon: 'Undo2',
    level: 'full',
    aliases: ['revert json', 'discard raw edit'],
    when: 'focus.raw',
    enabled() {
      const c = rawController();
      if (c === null) return { key: m.panelClosed };
      return c.dirty ? true : { key: m.unchanged };
    },
    run() {
      rawController()?.revert();
    },
  },
  {
    id: 'raw.copy',
    title: m.cmdCopy,
    category: 'Edit',
    icon: 'Braces',
    level: 'full',
    aliases: ['copy json', 'copy as rpe', 'copy rpe json', 'export snippet', '复制JSON'],
    menus: [
      { menu: 'lane.context', group: 'raw', order: 90 },
      { menu: 'motion.context', group: 'raw', order: 90 },
      { menu: 'lines.context', group: 'raw', order: 90 },
    ],
    enabled(ctx) {
      const ok = projectOpen(ctx);
      if (ok !== true) return ok;
      return selectionJson(ctx) !== null ? true : { key: m.noTarget };
    },
    async run(ctx) {
      const found = selectionJson(ctx);
      const store = ctx.store;
      if (found === null || store === null) {
        ctx.ui.toast({ key: m.noTarget }, { kind: 'warning' });
        return;
      }
      const label = targetLabel(found.target, store.doc, { t: (k, p) => ctx.i18n.t(k, p) });
      try {
        await navigator.clipboard.writeText(found.text);
        ctx.ui.toast({ key: m.copied, params: { target: label } }, { kind: 'success' });
      } catch (err) {
        ctx.ui.toast(
          { key: m.copyFailed, params: { reason: err instanceof Error ? err.message : String(err) } },
          { kind: 'error' },
        );
      }
    },
  },
  {
    id: 'raw.format',
    title: m.cmdFormat,
    category: 'View',
    icon: 'WrapText',
    level: 'full',
    aliases: ['pretty print', 'format json', 'compact json'],
    checked: (ctx) => ctx.settings.value.ext[RAW_COMPACT_KEY] === true,
    run(ctx) {
      ctx.settings.setExt(RAW_COMPACT_KEY, ctx.settings.value.ext[RAW_COMPACT_KEY] !== true);
    },
  },
];

const APPLY_KEY: KeyBinding = { chord: 'Ctrl+Enter', command: 'raw.apply', when: 'focus.raw' };

export const RAW_KEYBINDINGS: Readonly<Record<KeymapPresetId, readonly KeyBinding[]>> = {
  default: [APPLY_KEY],
  rpe: [APPLY_KEY],
};

const SETTINGS: readonly SettingDef[] = [
  {
    path: 'ext.raw.compact',
    title: m.settingCompact,
    description: m.settingCompactHelp,
    level: 'full',
    kind: 'boolean',
  },
  {
    path: 'ext.raw.shortestNumbers',
    title: m.settingShortest,
    description: m.settingShortestHelp,
    level: 'full',
    kind: 'boolean',
  },
];

const HELP: readonly HelpTopicDef[] = [
  { id: 'raw', title: m.helpTitle, body: m.helpBody, level: 'full', showMe: { panel: 'raw' } },
];

export default {
  id: 'raw',
  commands: RAW_COMMANDS,
  keybindings: RAW_KEYBINDINGS,
  panels: [
    {
      id: 'raw',
      title: m.panelTitle,
      icon: 'Braces',
      slot: 'chart.dock',
      order: 50,
      level: 'full',
      component: RawPanel,
    },
  ],
  settings: SETTINGS,
  helpTopics: HELP,
} satisfies FeatureModule;
